/**
 * ChallengeComments — Threaded comment section for challenges
 * 
 * Rules:
 *   - Only users with ≥1 project can comment (shows helpful prompt otherwise)
 *   - Challenge owner can delete any comment
 *   - Threaded replies with visual indent + connector lines
 *   - Collapsible by default, opens on click
 * 
 * Usage inside ChallengeDetailOverlay:
 *   <ChallengeComments
 *     challengeId={challenge.id}
 *     challengeOwnerId={challenge.requester_id || challenge.requesterId}
 *   />
 * 
 * Place at: src/components/challenges/ChallengeComments.jsx
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle, ChevronDown, Send, Trash2, CornerDownRight,
  Reply, AlertCircle, Sparkles,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';

// ── Helpers ──

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const s = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  if (s < 2592000) return `${Math.floor(s / 604800)}w ago`;
  return `${Math.floor(s / 2592000)}mo ago`;
};

// ── Single Comment ──

const CommentBubble = ({
  comment,
  depth = 0,
  currentUserId,
  challengeOwnerId,
  onReply,
  onDelete,
}) => {
  const isAuthor = currentUserId && comment.user?.id === currentUserId;
  const isChallengeOwner = currentUserId && currentUserId === challengeOwnerId;
  const canDelete = isAuthor || isChallengeOwner;
  const isDeleted = comment.isDeleted;
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className={cn('relative', depth > 0 && 'ml-6 sm:ml-8')}>
      {/* Threading connector line */}
      {depth > 0 && (
        <div className="absolute left-[-16px] sm:left-[-20px] top-0 bottom-0 w-px bg-zinc-800/60" />
      )}
      {depth > 0 && (
        <div className="absolute left-[-16px] sm:left-[-20px] top-5 w-3 sm:w-4 h-px bg-zinc-800/60" />
      )}

      <div className={cn(
        'group py-2.5',
        depth === 0 && 'border-b border-zinc-800/30 last:border-0',
      )}>
        {/* Author row */}
        <div className="flex items-center gap-2 mb-1.5">
          {/* Avatar */}
          <div className="w-6 h-6 rounded-full flex-shrink-0 overflow-hidden bg-zinc-800 flex items-center justify-center">
            {comment.user?.profilePictureUrl ? (
              <img src={comment.user.profilePictureUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-[10px] font-semibold text-zinc-500">
                {comment.user?.username?.[0]?.toUpperCase() || '?'}
              </span>
            )}
          </div>

          <span className="text-[12px] font-semibold text-zinc-300">
            @{comment.user?.username || 'unknown'}
          </span>

          {/* Challenge owner badge */}
          {comment.user?.id === challengeOwnerId && (
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20">
              OP
            </span>
          )}

          <span className="text-[10px] text-zinc-600">{timeAgo(comment.createdAt)}</span>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Actions (visible on hover/focus) */}
          {!isDeleted && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => onReply?.(comment)}
                className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50 transition-colors"
              >
                <Reply className="w-3 h-3" />
                Reply
              </button>
              {canDelete && (
                <>
                  {confirmDelete ? (
                    <button
                      onClick={() => { onDelete?.(comment.id); setConfirmDelete(false); }}
                      className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] text-red-400 bg-red-500/10 border border-red-500/20 transition-colors"
                    >
                      Confirm
                    </button>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(true)}
                      className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] text-zinc-600 hover:text-red-400 hover:bg-red-500/5 transition-colors"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <p className={cn(
          'text-[13px] leading-relaxed whitespace-pre-wrap break-words',
          isDeleted ? 'text-zinc-600 italic' : 'text-zinc-400',
        )}>
          {comment.content}
        </p>
      </div>

      {/* Nested replies */}
      {comment.replies?.length > 0 && (
        <div className="relative">
          {comment.replies.map(reply => (
            <CommentBubble
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              currentUserId={currentUserId}
              challengeOwnerId={challengeOwnerId}
              onReply={onReply}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Compose Box ──

const ComposeBox = ({ onSubmit, replyingTo, onCancelReply, disabled, placeholder }) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (replyingTo && inputRef.current) {
      inputRef.current.focus();
    }
  }, [replyingTo]);

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed, replyingTo?.id);
      setText('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-zinc-800/40 pt-3">
      {/* Replying-to indicator */}
      {replyingTo && (
        <div className="flex items-center gap-2 mb-2 px-1">
          <CornerDownRight className="w-3 h-3 text-zinc-600" />
          <span className="text-[11px] text-zinc-500">
            Replying to <span className="text-zinc-400 font-medium">@{replyingTo.user?.username}</span>
          </span>
          <button
            onClick={onCancelReply}
            className="text-[10px] text-zinc-600 hover:text-zinc-400 ml-auto transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Share a thought...'}
          disabled={disabled || submitting}
          rows={1}
          className="flex-1 min-h-[36px] max-h-[100px] px-3 py-2 bg-zinc-800/50 border border-zinc-700/50
                     rounded-xl text-[13px] text-zinc-300 placeholder:text-zinc-600
                     focus:outline-none focus:ring-1 focus:ring-amber-500/30 focus:border-amber-500/30
                     resize-none transition-all disabled:opacity-50"
          style={{ fieldSizing: 'content' }}
        />
        <button
          onClick={handleSubmit}
          disabled={!text.trim() || submitting || disabled}
          className={cn(
            'flex items-center justify-center w-9 h-9 rounded-xl transition-all active:scale-[0.95] flex-shrink-0',
            text.trim()
              ? 'bg-amber-500 text-zinc-900 hover:bg-amber-400'
              : 'bg-zinc-800 text-zinc-600 cursor-not-allowed',
          )}
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>

      <p className="text-[10px] text-zinc-700 mt-1.5 px-1">
        Shift+Enter for new line
      </p>
    </div>
  );
};

// ── Main Component ──

const ChallengeComments = ({ challengeId, challengeOwnerId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [userHasProjects, setUserHasProjects] = useState(null); // null = unknown
  const [error, setError] = useState(null);

  // Fetch comments when opened
  useEffect(() => {
    if (!open || !challengeId) return;
    let cancelled = false;

    const fetchComments = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/challenges/${challengeId}/comments`);
        if (!cancelled) {
          setComments(res.data?.comments || []);
        }
      } catch (err) {
        if (!cancelled) setError('Could not load comments');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchComments();
    return () => { cancelled = true; };
  }, [open, challengeId]);

  // Check if user has projects (for the gate)
  useEffect(() => {
    if (!user?.id || userHasProjects !== null) return;
    let cancelled = false;

    const check = async () => {
      try {
        const res = await api.get('/projects', { params: { user_id: user.id, per_page: 1 } });
        const projects = res.data?.projects || res.data || [];
        if (!cancelled) setUserHasProjects(projects.length > 0);
      } catch {
        if (!cancelled) setUserHasProjects(false);
      }
    };

    check();
    return () => { cancelled = true; };
  }, [user?.id, userHasProjects]);

  const handlePostComment = useCallback(async (content, parentCommentId) => {
    try {
      const res = await api.post(`/challenges/${challengeId}/comments`, {
        content,
        parentCommentId,
      });

      // Refetch to get proper threading
      const refreshRes = await api.get(`/challenges/${challengeId}/comments`);
      setComments(refreshRes.data?.comments || []);
      setReplyingTo(null);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to post comment';
      throw new Error(msg);
    }
  }, [challengeId]);

  const handleDelete = useCallback(async (commentId) => {
    try {
      await api.delete(`/challenge-comments/${commentId}`);
      // Refetch
      const res = await api.get(`/challenges/${challengeId}/comments`);
      setComments(res.data?.comments || []);
    } catch {
      // Silently fail
    }
  }, [challengeId]);

  const commentCount = comments.length;

  return (
    <div className="border-t border-zinc-800/40">
      {/* Toggle header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2.5 px-5 sm:px-6 py-3.5 text-left hover:bg-zinc-800/20 transition-colors"
      >
        <MessageCircle className="w-4 h-4 text-amber-400/70" />
        <span className="text-[13px] font-medium text-zinc-300 flex-1">
          Discussion
          {commentCount > 0 && (
            <span className="ml-1.5 text-[11px] text-zinc-600 font-normal">
              {commentCount}
            </span>
          )}
        </span>
        <ChevronDown className={cn(
          'w-3.5 h-3.5 text-zinc-600 transition-transform duration-200',
          open && 'rotate-180',
        )} />
      </button>

      {/* Expanded content */}
      {open && (
        <div className="px-5 sm:px-6 pb-4 animate-in fade-in slide-in-from-top-1 duration-200">
          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-6">
              <div className="w-5 h-5 border-2 border-zinc-700 border-t-amber-500 rounded-full animate-spin" />
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex items-center gap-2 py-4 text-[12px] text-red-400/70">
              <AlertCircle className="w-3.5 h-3.5" />
              {error}
            </div>
          )}

          {/* Comments list */}
          {!loading && !error && (
            <>
              {comments.length === 0 ? (
                <div className="text-center py-6">
                  <MessageCircle className="w-8 h-8 text-zinc-800 mx-auto mb-2" />
                  <p className="text-[12px] text-zinc-600">
                    No comments yet. Be the first to share a thought.
                  </p>
                </div>
              ) : (
                <div className="mb-3">
                  {comments.map(comment => (
                    <CommentBubble
                      key={comment.id}
                      comment={comment}
                      depth={0}
                      currentUserId={user?.id}
                      challengeOwnerId={challengeOwnerId}
                      onReply={(c) => setReplyingTo(c)}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}

              {/* Compose box — or gate message */}
              {user ? (
                userHasProjects === false ? (
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-zinc-800/20 border border-zinc-800/30">
                    <Sparkles className="w-4 h-4 text-amber-400/60 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[12px] text-zinc-400 leading-relaxed">
                        Upload your first project to join the conversation.
                        Comments are for creators — your work is your entry ticket.
                      </p>
                    </div>
                  </div>
                ) : (
                  <ComposeBox
                    onSubmit={handlePostComment}
                    replyingTo={replyingTo}
                    onCancelReply={() => setReplyingTo(null)}
                    disabled={userHasProjects === null}
                    placeholder={replyingTo ? `Reply to @${replyingTo.user?.username}...` : 'Share a thought on this challenge...'}
                  />
                )
              ) : (
                <p className="text-[12px] text-zinc-600 text-center py-3">
                  Sign in to join the discussion.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChallengeComments;